/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, useEffect, useCallback } from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

/**
 * React hook that counts down the seconds remaining until `expiresAt`,
 * invoking `onExpired` once the countdown reaches zero.
 *
 * @param expiresAt Expiry timestamp in Unix seconds.
 * @param onExpired Callback fired when the remaining time hits zero.
 * @returns The remaining whole seconds, updated every second.
 */
export function useCountdownToExpiry(expiresAt: number, onExpired: () => void) {
  const getRemainingSeconds = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, expiresAt - now);
  }, [expiresAt]);

  const [remaining, setRemaining] = useState(getRemainingSeconds);

  useEffect(() => {
    setRemaining(getRemainingSeconds());
    const interval = setInterval(() => {
      const secs = getRemainingSeconds();
      setRemaining(secs);
      if (secs <= 0) {
        clearInterval(interval);
        onExpired();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [getRemainingSeconds, onExpired]);

  return remaining;
}

/**
 * Formats a duration in seconds as a `m:ss` clock string (e.g. `2:05`).
 *
 * @param seconds Whole seconds to format.
 * @returns The minutes:seconds string, zero-padded on the seconds.
 */
export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Renders the WhatsApp brand glyph as an inline SVG using `currentColor`.
 *
 * @param props Optional `className` applied to the root `svg`.
 * @returns The WhatsApp icon element.
 */
export function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/** Props for the {@link Steps} instructional list. */
interface StepsProps {
  t: (key: string) => string;
  isMobile: boolean;
  layout?: 'horizontal' | 'vertical';
}

/**
 * Renders the three numbered instructions for linking a WhatsApp account,
 * laid out horizontally (default) or vertically depending on `layout`.
 *
 * @param props Translation function, mobile flag, and layout orientation.
 * @returns The ordered step indicators with their labels.
 */
export function Steps({ t, isMobile, layout = 'horizontal' }: StepsProps) {
  const steps = [
    { num: 1, label: isMobile ? t('whatsapp.step1_mobile') : t('whatsapp.step1') },
    { num: 2, label: t('whatsapp.step2') },
    { num: 3, label: t('whatsapp.step3') },
  ];

  if (layout === 'vertical') {
    return (
      <div className="flex flex-col gap-(--space-3) w-full">
        {steps.map((step) => (
          <div key={step.num} className="flex items-center gap-(--space-3)">
            <span className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-white bg-[#25D366] text-xs shrink-0">
              {step.num}
            </span>
            <span className="text-sm text-(--text-secondary) leading-snug">
              {step.label}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn(
      'grid grid-cols-3 w-full',
      isMobile ? 'gap-(--space-4)' : 'gap-(--space-5)',
    )}>
      {steps.map((step) => (
        <div key={step.num} className="flex flex-col items-center gap-2.5">
          <span className={cn(
            'rounded-full flex items-center justify-center font-bold text-white bg-[#25D366]',
            isMobile ? 'w-8 h-8 text-sm' : 'w-8 h-8 text-sm',
          )}>
            {step.num}
          </span>
          <span className={cn(
            'text-(--text-secondary) text-center leading-snug',
            isMobile ? 'text-base' : 'text-sm',
          )}>
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Renders a horizontal progress bar for the remaining link validity, turning
 * amber in the final minute to signal imminent expiry.
 *
 * @param props Remaining and total seconds used to compute the fill width.
 * @returns The timer bar element.
 */
export function TimerBar({ remaining, total }: { remaining: number; total: number }) {
  const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
  const isLow = remaining < 60;

  return (
    <div className="w-full h-2 bg-(--border-subtle) rounded-full overflow-hidden">
      <div
        className={cn(
          'h-full rounded-full transition-all duration-1000 ease-linear',
          isLow ? 'bg-[var(--status-warning)]' : 'bg-[#25D366]',
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
